// Localização: frontend/src/components/Stripe/CheckoutForm.jsx
// Formulário de checkout usando Stripe Elements

import React, { useState, useEffect } from 'react';
import {
    CardElement,
    useStripe,
    useElements
} from '@stripe/react-stripe-js';
import { createCheckout } from '../../api/stripeApi';
import { CreditCard, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

const CheckoutForm = ({ planName, planValue, plano_id, onSuccess, onError }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [cardComplete, setCardComplete] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            // 1. Criar checkout session no backend
            const checkoutResponse = await createCheckout(plano_id);

            if (!checkoutResponse.success) {
                throw new Error(checkoutResponse.message || 'Erro ao criar checkout');
            }

            // Se for upgrade/downgrade, pode não ter client_secret (já está pago)
            if (checkoutResponse.is_upgrade && !checkoutResponse.client_secret) {
                // Upgrade/downgrade concluído sem necessidade de confirmação de pagamento
                setSuccess(true);
                setLoading(false);
                if (onSuccess) {
                    onSuccess({
                        subscription_id: checkoutResponse.subscription_id,
                        status: checkoutResponse.status,
                        is_upgrade: true,
                    });
                }
                return;
            }

            // Se não tem client_secret e não é upgrade, erro
            if (!checkoutResponse.client_secret) {
                throw new Error(checkoutResponse.message || 'Erro ao criar checkout: client_secret não fornecido');
            }

            const { client_secret, subscription_id } = checkoutResponse;

            // 2. Obter CardElement
            const cardElement = elements.getElement(CardElement);

            if (!cardElement) {
                throw new Error('Elemento de cartão não encontrado');
            }

            // 3. Criar payment method do cartão
            const { error: createPMError, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
            });

            if (createPMError) {
                setError(createPMError.message || 'Erro ao processar cartão');
                setLoading(false);
                if (onError) {
                    onError(createPMError);
                }
                return;
            }

            // 4. Confirmar pagamento com Stripe (para subscription, pode ser PaymentIntent ou SetupIntent)
            // Se client_secret começar com 'seti_', é SetupIntent, senão é PaymentIntent
            const isSetupIntent = client_secret.startsWith('seti_');

            let confirmResult;
            if (isSetupIntent) {
                // SetupIntent: confirmar e anexar payment method
                confirmResult = await stripe.confirmCardSetup(client_secret, {
                    payment_method: paymentMethod.id,
                });
            } else {
                // PaymentIntent: confirmar pagamento
                confirmResult = await stripe.confirmCardPayment(client_secret, {
                    payment_method: paymentMethod.id,
                });
            }

            if (confirmResult.error) {
                setError(confirmResult.error.message || 'Erro ao processar pagamento');
                setLoading(false);
                if (onError) {
                    onError(confirmResult.error);
                }
                return;
            }

            // 5. Verificar resultado
            const result = isSetupIntent ? confirmResult.setupIntent : confirmResult.paymentIntent;

            // Para subscriptions, o status pode ser 'succeeded' ou 'requires_action'
            // Se for 'requires_payment_method', significa que o payment method precisa ser anexado
            const successStatuses = ['succeeded', 'requires_action', 'requires_confirmation'];
            const requiresPaymentMethod = result?.status === 'requires_payment_method';

            if (result && (successStatuses.includes(result.status) || requiresPaymentMethod)) {
                // Se o payment method foi confirmado, a subscription será ativada automaticamente
                // pelo webhook do Stripe quando o payment intent for confirmado
                setSuccess(true);
                setLoading(false);

                if (onSuccess) {
                    onSuccess({
                        subscription_id,
                        payment_intent_id: result.id,
                        payment_method_id: paymentMethod.id,
                        status: result.status,
                    });
                }
            } else {
                throw new Error(`Status inesperado: ${result?.status || 'desconhecido'}`);
            }

        } catch (err) {
            setError(err.message || 'Erro ao processar pagamento. Tente novamente.');
            setLoading(false);
            if (onError) {
                onError(err);
            }
        }
    };

    // Handler para mudanças no CardElement
    const handleCardChange = (event) => {
        if (event.complete) {
            setCardComplete(true);
            setError(null);
        } else {
            setCardComplete(false);
            if (event.error) {
                setError(event.error.message);
            }
        }
    };

    // Opções de estilo para o CardElement
    const cardElementOptions = {
        style: {
            base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                    color: '#aab7c4',
                },
                fontFamily: 'system-ui, -apple-system, sans-serif',
            },
            invalid: {
                color: '#9e2146',
            },
        },
        hidePostalCode: true, // Ocultar campo de CEP (opcional)
    };


    if (!stripe || !elements) {
        return (
            <div className="space-y-6">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">{planName || 'Carregando...'}</h3>
                            <p className="text-sm text-gray-600">Plano selecionado</p>
                        </div>
                        <div className="text-right">
                            <p className="text-2xl font-bold text-emerald-600">
                                {planValue ? `R$ ${planValue.toFixed(2).replace('.', ',')}` : '...'}
                            </p>
                            <p className="text-xs text-gray-500">por mês</p>
                        </div>
                    </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                        <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                        <div className="flex-1">
                            <p className="text-sm text-blue-800 font-medium">Carregando formulário de pagamento...</p>
                            <p className="text-xs text-blue-600 mt-1">
                                Se isso demorar muito, verifique se a chave pública do Stripe está configurada no .env
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="text-sm font-medium text-yellow-800">Troubleshooting</p>
                            <p className="text-xs text-yellow-700 mt-1">
                                Abra o console do navegador (F12) para verificar erros. Certifique-se de que a variável{' '}
                                <code className="bg-yellow-100 px-1 rounded">
                                    {process.env.NODE_ENV === 'production' 
                                        ? 'REACT_APP_STRIPE_PUBLIC_KEY' 
                                        : 'REACT_APP_STRIPE_TEST_PUBLIC_KEY'}
                                </code> está configurada no arquivo <code className="bg-yellow-100 px-1 rounded">.env</code> do frontend.
                            </p>
                            <p className="text-xs text-yellow-600 mt-2">
                                <strong>⚠️ Em produção:</strong> As variáveis REACT_APP_* devem estar configuradas no momento do BUILD.
                                Se você alterou o .env após o build, será necessário fazer um novo build.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Informações do Plano */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">{planName || 'Plano'}</h3>
                        <p className="text-sm text-gray-600">Plano selecionado</p>
                    </div>
                    <div className="text-right">
                        <p className="text-2xl font-bold text-emerald-600">
                            R$ {planValue ? planValue.toFixed(2).replace('.', ',') : '0,00'}
                        </p>
                        <p className="text-xs text-gray-500">por mês</p>
                    </div>
                </div>
            </div>

            {/* Campo de Cartão */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Informações do Cartão
                </label>
                <div className="border border-gray-300 rounded-lg p-4 bg-white focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-200 transition-all">
                    <CardElement
                        options={cardElementOptions}
                        onChange={handleCardChange}
                    />
                </div>
                <p className="mt-2 text-xs text-gray-500">
                    Seus dados de pagamento são processados de forma segura pelo Stripe
                </p>
                {cardComplete && (
                    <p className="mt-1 text-xs text-emerald-600 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Cartão válido
                    </p>
                )}
            </div>

            {/* Mensagem de Erro */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-medium text-red-800">Erro no pagamento</p>
                        <p className="text-sm text-red-600 mt-1">{error}</p>
                    </div>
                </div>
            )}

            {/* Mensagem de Sucesso */}
            {success && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-medium text-green-800">Pagamento processado!</p>
                        <p className="text-sm text-green-600 mt-1">
                            Redirecionando...
                        </p>
                    </div>
                </div>
            )}

            {/* Botão de Submit */}
            <button
                type="submit"
                disabled={!stripe || !cardComplete || loading || success}
                className="w-full bg-emerald-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
                {loading ? (
                    <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Processando pagamento...</span>
                    </>
                ) : success ? (
                    <>
                        <CheckCircle2 className="h-5 w-5" />
                        <span>Pagamento confirmado!</span>
                    </>
                ) : (
                    <>
                        <CreditCard className="h-5 w-5" />
                        <span>Confirmar Pagamento</span>
                    </>
                )}
            </button>

            {/* Informações de Segurança */}
            <div className="text-center">
                <p className="text-xs text-gray-500">
                    🔒 Pagamento seguro processado pelo Stripe
                </p>
            </div>
        </form>
    );
};

export default CheckoutForm;

